/**
 * `ConversationView` — the shared, proven cross-channel conversation panel.
 *
 * Extracted VERBATIM from the member detail page's Conversation panel (the
 * battle-tested one): identical message-log rendering (per-channel colour
 * bubbles, reactions, search + highlight, scroll pinning) and the same
 * multi-channel reply composer (WhatsApp / Gmail / Instagram). Per-page
 * differences are injected:
 *   - the caller passes the merged `messages` + the three reply targets;
 *   - the caller passes an optional `handoverSlot` (the member page's "Pause AI"
 *     bar; the PR contact profile has no AI auto-responder, so it passes none).
 *
 * Mounted by THREE pages: `/members/:id` (MemberDetail — its former drifted
 * inline copy was collapsed onto this component), `/inbox/threads/:id`
 * (InboxThreadDetail) and `/pr/people/:id` (PrPersonDetail). An edit here
 * reaches all three.
 */

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { studioFormat } from "../lib/studio-tz";
import { useDispatch } from "../lib/use-dispatch";
import {
  availableReplyChannels,
  defaultReplyChannel,
  type ReplyChannel,
} from "../lib/reply-channels";
import type { MemberDetailMessage, OutreachEmailTracking } from "../lib/api";
import { EmailHtmlModal } from "./EmailHtmlModal";
import { PopMenu } from "./PopMenu";
import { isOpenableEmail } from "../lib/email-thread";
import { emptyBubbleText } from "../lib/message-bubble";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : studioFormat(DATETIME_OPTS).format(d);
}

/** Human label for a message channel — falls back to the raw value for future channels. */
function channelLabel(channel: string): string {
  switch (channel) {
    case "whatsapp":
      return "WhatsApp";
    case "email":
      return "Email";
    case "instagram":
      return "Instagram";
    default:
      return channel;
  }
}

function highlightBody(body: string, term: string): ReactNode {
  if (term.length === 0) return body;
  const lower = body.toLowerCase();
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  for (;;) {
    const at = lower.indexOf(term, i);
    if (at === -1) {
      out.push(body.slice(i));
      break;
    }
    if (at > i) out.push(body.slice(i, at));
    out.push(
      <mark key={key} className="member-chat-hit">
        {body.slice(at, at + term.length)}
      </mark>,
    );
    key += 1;
    i = at + term.length;
  }
  return out;
}

function SearchIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <circle cx="7" cy="7" r="4.5" />
      <line x1="10.6" y1="10.6" x2="14" y2="14" />
    </svg>
  );
}

/**
 * Default email-reply subject: "Re: <last inbound email subject>" when one
 * exists, else a generic default (the composer keeps it editable).
 */
export function deriveEmailReplySubject(
  messages: ReadonlyArray<MemberDetailMessage>,
): string {
  const GENERIC = "Re: your recent message";
  const PREFIX = "subject: ";
  const lastInboundEmail = messages.find(
    (m) =>
      m.channel === "email" &&
      m.dir === "in" &&
      (m.body ?? "").toLowerCase().startsWith(PREFIX),
  );
  if (lastInboundEmail === undefined) return GENERIC;
  const firstLine = (lastInboundEmail.body ?? "").split("\n")[0] ?? "";
  const subject = firstLine.slice(PREFIX.length).trim();
  if (subject.length === 0) return GENERIC;
  return subject.toLowerCase().startsWith("re:") ? subject : `Re: ${subject}`;
}

/**
 * The staff reply composer — multi-channel (WhatsApp · Email · Instagram).
 *
 * A channel selector picks which channel the reply goes out on; each channel
 * dispatches the SAME canonical action a webhook/PA would, as `human:staff`:
 *   - WhatsApp  → `send_whatsapp_message({to: phone, body})`
 *   - Email     → `send_gmail({to: email, subject, text_body})`
 *   - Instagram → `send_instagram_dm({igsid, body})` — REPLY-ONLY inside Meta's
 *                 24h window; outside it the action returns `instagram_window_closed`,
 *                 surfaced verbatim below (no pre-check).
 *
 * A channel option is disabled when there's no reply target for it. After a
 * successful send `onSent` fires so the caller re-fetches.
 */
export function ConversationReplyComposer({
  phone,
  email,
  igsid,
  emailSubjectDefault,
  onSent,
}: {
  readonly phone: string | null;
  readonly email: string | null;
  readonly igsid: string | null;
  readonly emailSubjectDefault: string;
  readonly onSent: () => void;
}): JSX.Element {
  const targets = { phone, email, igsid };
  const available = availableReplyChannels(targets);
  const [channel, setChannel] = useState<ReplyChannel>(() =>
    defaultReplyChannel(targets),
  );
  const [draft, setDraft] = useState<string>("");
  const [subject, setSubject] = useState<string>(emailSubjectDefault);

  // All three dispatch hooks are instantiated unconditionally (hooks can't be
  // conditional); only the selected channel's hook is driven on send.
  const waDispatch = useDispatch<
    { readonly to: string; readonly body: string },
    { readonly message_id: string }
  >("send_whatsapp_message");
  const emailDispatch = useDispatch<
    {
      readonly to: string;
      readonly subject: string;
      readonly text_body: string;
    },
    { readonly message_id: string }
  >("send_gmail");
  const igDispatch = useDispatch<
    { readonly igsid: string; readonly body: string },
    { readonly message_id: string }
  >("send_instagram_dm");
  const active =
    channel === "whatsapp"
      ? waDispatch
      : channel === "email"
        ? emailDispatch
        : igDispatch;

  // If the selected channel loses its target (e.g. after a refetch), snap to
  // the first still-available channel so the composer never sits on a dead one.
  useEffect(() => {
    if (available.length > 0 && !available.includes(channel)) {
      setChannel(available[0] as ReplyChannel);
    }
  }, [available, channel]);

  const trimmedBody = draft.trim();
  const emailReady = channel !== "email" || subject.trim().length > 0;
  const canSend =
    available.includes(channel) && trimmedBody.length > 0 && emailReady;

  const send = async (): Promise<void> => {
    if (active.loading || !canSend) return;
    let r: { ok: boolean };
    if (channel === "whatsapp" && phone !== null) {
      r = await waDispatch.run({ to: phone, body: trimmedBody });
    } else if (channel === "email" && email !== null) {
      r = await emailDispatch.run({
        to: email,
        subject: subject.trim(),
        text_body: trimmedBody,
      });
    } else if (channel === "instagram" && igsid !== null) {
      r = await igDispatch.run({ igsid, body: trimmedBody });
    } else {
      return;
    }
    if (r.ok) {
      setDraft("");
      onSent();
    }
  };

  if (available.length === 0) {
    return (
      <p className="member-reply-empty">
        Add a phone, email, or Instagram link to reply.
      </p>
    );
  }

  const placeholder =
    channel === "whatsapp"
      ? `Reply on WhatsApp to ${phone ?? ""}…`
      : channel === "email"
        ? `Reply by email to ${email ?? ""}…`
        : "Reply on Instagram…";

  const CHANNELS: ReadonlyArray<{ ch: ReplyChannel; missing: string }> = [
    { ch: "whatsapp", missing: "No phone on file" },
    { ch: "email", missing: "No email on file" },
    { ch: "instagram", missing: "No Instagram link on file" },
  ];

  return (
    <div className="member-reply">
      {channel === "email" ? (
        <input
          className="member-reply-subject"
          type="text"
          value={subject}
          placeholder="Subject"
          onChange={(e) => setSubject(e.target.value)}
        />
      ) : null}
      <div className="member-reply-row">
        <textarea
          className="member-reply-input"
          value={draft}
          placeholder={placeholder}
          rows={1}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter sends; plain Enter inserts a newline.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {/* Channel sits BETWEEN the draft and Send, reading as one sentence:
            "this text · by WhatsApp · send". As a three-abreast pill row above
            the box it spent a whole line permanently displaying a choice that
            changes rarely, and on a card whose height is budgeted against its
            twin that line was expensive. */}
        <PopMenu
          label={`Reply channel — ${channelLabel(channel)}`}
          triggerClassName="member-reply-chan-trigger"
          align="right"
          trigger={
            <>
              {channelLabel(channel)}
              <span className="member-reply-caret" aria-hidden="true">
                ▾
              </span>
            </>
          }
        >
          {(close) => (
            <>
              {CHANNELS.map(({ ch, missing }) => {
                const enabled = available.includes(ch);
                return (
                  <button
                    key={ch}
                    type="button"
                    role="menuitemradio"
                    aria-checked={channel === ch}
                    className="member-reply-chan-row"
                    disabled={!enabled}
                    title={enabled ? undefined : missing}
                    onClick={() => {
                      setChannel(ch);
                      close();
                    }}
                  >
                    <span className="member-reply-chan-tick" aria-hidden="true">
                      {channel === ch ? "✓" : ""}
                    </span>
                    {channelLabel(ch)}
                  </button>
                );
              })}
            </>
          )}
        </PopMenu>
        <button
          type="button"
          className="member-reply-btn"
          disabled={active.loading || !canSend}
          onClick={() => void send()}
        >
          {active.loading ? "Sending…" : "Send"}
        </button>
      </div>
      {active.error !== null ? (
        <span className="member-reply-err">{active.error.message}</span>
      ) : null}
    </div>
  );
}

/**
 * Delivery/engagement chips for a tracked (Resend / hello@) outbound email.
 * Bad news dominates: a bounce or a spam complaint is the thing staff must
 * notice, so it renders first and in the alert colour; delivered is neutral;
 * opened/clicked are the good news. No events yet → nothing renders (the meta
 * line already says the mail was sent).
 */
function EmailTrackingChips({
  t,
}: {
  readonly t: OutreachEmailTracking;
}): JSX.Element | null {
  const chips: Array<{
    key: string;
    cls: "bad" | "ok" | "good";
    label: string;
    at: string;
  }> = [];
  if (t.bounced_at !== null) {
    chips.push({ key: "bounced", cls: "bad", label: "Bounced", at: t.bounced_at });
  }
  if (t.complained_at !== null) {
    chips.push({
      key: "complained",
      cls: "bad",
      label: "Marked spam",
      at: t.complained_at,
    });
  }
  if (t.delivered_at !== null && t.bounced_at === null) {
    chips.push({
      key: "delivered",
      cls: "ok",
      label: "Delivered",
      at: t.delivered_at,
    });
  }
  if (t.opened_at !== null) {
    chips.push({
      key: "opened",
      cls: "good",
      label: t.opened_count > 1 ? `Opened ×${t.opened_count}` : "Opened",
      at: t.opened_at,
    });
  }
  if (t.clicked_at !== null) {
    chips.push({
      key: "clicked",
      cls: "good",
      label: t.clicked_count > 1 ? `Clicked ×${t.clicked_count}` : "Clicked",
      at: t.clicked_at,
    });
  }
  if (chips.length === 0) return null;
  return (
    <div className="member-msg-tracking">
      {chips.map((c) => (
        <span
          key={c.key}
          className={`member-msg-track-chip member-msg-track-${c.cls}`}
          title={fmtDateTime(c.at)}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

/**
 * The full conversation panel: header + search + optional handover slot + the
 * per-channel message log + the reply composer. Driven entirely by props so it
 * works for a member OR an outreach contact. Owns its own scroll (pinned to the
 * newest message when browsing; jumped to the first match while searching;
 * re-pinned on resize).
 */
export function ConversationView({
  messages,
  phone,
  email,
  igsid,
  emailSubjectDefault,
  onSent,
  handoverSlot,
  inboundLabel = "Them",
  emailTracking,
  instagramAgent,
  readOnly = false,
}: {
  readonly messages: ReadonlyArray<MemberDetailMessage>;
  readonly phone: string | null;
  readonly email: string | null;
  readonly igsid: string | null;
  readonly emailSubjectDefault: string;
  readonly onSent: () => void;
  /** Optional control rendered in the card header, right of the message count
   *  (the member page's AI handover toggle). */
  readonly handoverSlot?: ReactNode;
  /**
   * Hide the reply composer (2026-08-06 — the `/inbox` channel tabs).
   *
   * That board is a VIEW over every WhatsApp/Instagram conversation, including
   * senders who match no member and no PR contact. A composer there would be
   * the wrong place to answer from: replying belongs on the member or PR card,
   * where the person, their credits and their AI switch are all in front of
   * you. Defaults to `false`, so the three pages that mounted this component
   * before are byte-identical.
   */
  readonly readOnly?: boolean;
  /** Label for an inbound message's sender — "Member" on the member page,
   *  "Them" (default) for an outreach contact. */
  readonly inboundLabel?: string;
  /**
   * Per-message Resend tracking keyed by `vendor_message_id` (2026-07-22 —
   * the PR page's hello@ sends). When supplied, outbound email bubbles whose
   * id has an entry get delivered/opened/clicked/bounced chips. Pages that
   * don't pass it (the member page today) render exactly as before.
   */
  readonly emailTracking?: Readonly<Record<string, OutreachEmailTracking>>;
  /**
   * Per-contact "Instagram AI" on/off chip (2026-07-24). Supplied only when
   * the contact HAS an `instagram_dm_threads` row (the switch needs a thread
   * to flip). `mode='notify'` = AI off — the default for every contact;
   * `mode='agent'` = the booking agent replies autonomously inside the 24h
   * window. `onToggle` dispatches `set_instagram_thread_mode` and refetches.
   */
  readonly instagramAgent?: {
    readonly mode: "notify" | "agent";
    readonly busy: boolean;
    readonly onToggle: (next: "notify" | "agent") => void;
  };
}): JSX.Element {
  const [query, setQuery] = useState<string>("");
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  /** Gmail message id of the email being viewed in full, or null when closed. */
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Scroll-preservation state: only auto-scroll on first load / a new message
  // while already at the bottom, so a refresh never moves the reader's position.
  const prevMsgCountRef = useRef<number>(0);
  const atBottomRef = useRef<boolean>(true);
  const handleChatScroll = (): void => {
    const el = chatRef.current;
    if (el !== null) {
      atBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    }
  };

  const term = query.trim().toLowerCase();
  const openSearch = (): void => {
    setSearchOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const closeIfEmpty = (): void => {
    if (query.trim() === "") setSearchOpen(false);
  };
  // Server returns newest-first; show oldest-first like a chat log.
  const ordered = messages.slice().reverse();
  const shown =
    term.length === 0
      ? ordered
      : ordered.filter((msg) => (msg.body ?? "").toLowerCase().includes(term));

  // Preserve the reader's scroll position across refreshes: jump to the first
  // match while searching; on first load / a new message while already at the
  // bottom → pin to newest; otherwise leave scroll untouched.
  useEffect(() => {
    const el = chatRef.current;
    if (el === null) return;
    if (term.length > 0) {
      el.scrollTop = 0;
      return;
    }
    const grew = messages.length > prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;
    if (grew && atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      atBottomRef.current = true;
    }
  }, [term, messages]);

  useEffect(() => {
    const el = chatRef.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (term.length === 0 && atBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [term]);

  return (
    <section className="panel member-comm-panel">
      <div className="member-comm-head">
        <h2>Conversation</h2>
        {handoverSlot}
        {instagramAgent !== undefined ? (
          <button
            type="button"
            className={`ig-agent-toggle ${
              instagramAgent.mode === "agent"
                ? "ig-agent-toggle-on"
                : "ig-agent-toggle-off"
            }`}
            disabled={instagramAgent.busy}
            title={
              instagramAgent.mode === "agent"
                ? "The AI replies to this contact's Instagram DMs automatically. Click to turn it off — their DMs will alert staff instead."
                : "The AI does not reply to this contact's Instagram DMs (the default) — inbound DMs alert staff. Click to let the AI reply automatically."
            }
            onClick={() =>
              instagramAgent.onToggle(
                instagramAgent.mode === "agent" ? "notify" : "agent",
              )
            }
          >
            Instagram AI:{" "}
            {instagramAgent.busy
              ? "…"
              : instagramAgent.mode === "agent"
                ? "On"
                : "Off"}
          </button>
        ) : null}
        {messages.length > 0 ? (
          <div className="member-chat-search">
            {!searchOpen ? (
              <button
                type="button"
                className="member-chat-search-toggle"
                title="Search conversation"
                aria-label="Search conversation"
                onClick={openSearch}
              >
                <SearchIcon />
              </button>
            ) : (
              <>
                <input
                  ref={inputRef}
                  type="search"
                  className="member-chat-search-input"
                  value={query}
                  placeholder="Search conversation…"
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setQuery(e.target.value)}
                  onBlur={closeIfEmpty}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setQuery("");
                      setSearchOpen(false);
                    }
                  }}
                />
                {term.length > 0 ? (
                  <span className="member-chat-search-count">
                    {shown.length} match{shown.length === 1 ? "" : "es"}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="member-chat-search-close"
                  aria-label="Close search"
                  onClick={() => {
                    setQuery("");
                    setSearchOpen(false);
                  }}
                >
                  ×
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>
      {messages.length === 0 ? (
        <p className="empty">No messages.</p>
      ) : (
        <>
          {/* No channel legend: every message already carries its own channel
              label in its header, so the strip restated it for the whole log. */}
          <div className="member-chat" ref={chatRef} onScroll={handleChatScroll}>
            {shown.length === 0 ? (
              <p className="empty">No messages match “{query.trim()}”.</p>
            ) : (
              shown.map((msg, i) => (
                <div
                  key={`${msg.ts}:${i}`}
                  className={`member-msg member-msg-${msg.dir} member-msg-ch-${msg.channel}${
                    isOpenableEmail(msg) ? " member-msg-openable" : ""
                  }${msg.dir === "out" && msg.status === "failed" ? " member-msg-failed" : ""}`}
                  {...(isOpenableEmail(msg)
                    ? {
                        role: "button" as const,
                        tabIndex: 0,
                        title: "Open the full email",
                        onClick: () => {
                          // Don't hijack a text selection — dragging across a
                          // bubble to copy a line would otherwise open the modal
                          // and throw the selection away.
                          if (
                            (window.getSelection()?.toString() ?? "").length > 0
                          ) {
                            return;
                          }
                          setOpenEmailId(msg.vendor_message_id);
                        },
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setOpenEmailId(msg.vendor_message_id);
                          }
                        },
                      }
                    : {})}
                >
                  <div className="member-msg-meta">
                    <span
                      className={`member-msg-chan member-msg-chan-${msg.channel}`}
                    >
                      {channelLabel(msg.channel)}
                    </span>{" "}
                    ·{" "}
                    {msg.dir === "in"
                      ? inboundLabel
                      : msg.source === "whatsapp_manual_send" ||
                          msg.source === "instagram_manual_send"
                        ? // A human typed this in the vendor's own app (WhatsApp
                          // Business App coexistence / Instagram app); ingested
                          // from its echo, not sent through Layer.
                          "Studio (app) 📱"
                        : "Studio"}{" "}
                    · {fmtDateTime(msg.ts)}
                  </div>
                  <div className="member-msg-body">
                    {msg.body !== null && (msg.body ?? "").length > 0
                      ? highlightBody(msg.body, term)
                      : emptyBubbleText(msg)}
                  </div>
                  {msg.dir === "out" && msg.status === "failed" ? (
                    <div
                      className="member-msg-failed-badge"
                      title="The send was rejected by the channel — the recipient never received this message."
                    >
                      ⚠ Failed — not delivered
                    </div>
                  ) : null}
                  {(() => {
                    if (
                      msg.dir !== "out" ||
                      msg.channel !== "email" ||
                      msg.vendor_message_id === null
                    ) {
                      return null;
                    }
                    const t = emailTracking?.[msg.vendor_message_id];
                    return t !== undefined ? <EmailTrackingChips t={t} /> : null;
                  })()}
                  {msg.reactions.length > 0 ? (
                    <div
                      className="member-msg-reactions"
                      title={`Reacted ${msg.reactions
                        .map((rx) => rx.emoji)
                        .join(" ")}`}
                    >
                      {msg.reactions.map((rx, ri) => (
                        <span
                          key={`${rx.at}:${ri}`}
                          className="member-msg-reaction"
                        >
                          {rx.emoji}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </>
      )}
      {readOnly ? null : (
        <ConversationReplyComposer
          phone={phone}
          email={email}
          igsid={igsid}
          emailSubjectDefault={emailSubjectDefault}
          onSent={onSent}
        />
      )}
      {openEmailId !== null ? (
        <EmailHtmlModal
          messageId={openEmailId}
          onClose={() => setOpenEmailId(null)}
        />
      ) : null}
    </section>
  );
}
